/**
 * The queue — Step 17. The rules that make it feel right, all live now:
 *  - every filter is URL state (shareable, refresh-safe, Back-friendly);
 *  - rows open the full detail page (no peek panel — Kai's call);
 *  - saved views SEED the URL and never replace it: explicit params in a
 *    shared link win, and a modified view shows Save / Save as / Reset;
 *  - keyboard: j/k move, Enter opens, x selects, Esc clears;
 *  - selection is transient state (never the URL — a link that pre-selects 40
 *    tickets for a colleague is a footgun); bulk actions ride a floating pill
 *    and report partial failure honestly via toast.
 * (TanStack Table arrives with Step 42's column chooser — a fixed-column row
 * list needs no table engine yet.)
 *
 * Draft-first on purpose: the only free text on this page is the queue SEARCH
 * box, which is URL state, not a draft. The composer lives on the ticket.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Columns3,
  PanelLeftOpen,
  Plus,
  RefreshCw,
  SlidersHorizontal,
  Bookmark,
  Search,
  X,
  Download,
  Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useConfirm } from '../../lib/confirm';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { cn } from '../../lib/utils';
import { useDebouncedUrlState, useUrlListState, useUrlPatch, useUrlState } from '../../lib/urlState';
import { qk } from '../../lib/queryClient';
import { apiFetch, apiSend } from '../../lib/api';
import {
  ticketPath,
  PRIORITY_META,
  STATUS_BADGE,
  ticketsApi,
  type TicketList,
  type TicketRow,
} from '../../features/tickets/api';
import {
  BUILTIN_VIEWS,
  VIEW_TRANSIENT_KEYS,
  useViews,
  DEFAULT_PAGE_SIZE,
  canonicalOf,
  canonicalQuery,
  type SavedView,
} from '../../features/tickets/views';
import { ViewRail } from './ViewRail';
import { ViewEditModal } from './ViewEditModal';
import { QueueFilterPanel } from './QueueFilterPanel';
import { ColumnChooser } from './ColumnChooser';
import {
  COLUMN_MAX,
  COLUMN_MIN,
  GUTTER_PX,
  COLUMN_RULE,
  COLUMN_DIVIDER,
  COLUMN_GAP_PX,
  TABLE_PAD_PX,
  DEFAULT_COLUMNS,
  gridTemplate,
  minTableWidth,
  parseWidths,
  resolveColumns,
  serialiseWidths,
} from '../../features/tickets/columns';

/** Built-ins are CODE, not rows: they cannot be starred, renamed or deleted,
 *  and offering those actions would be buttons that fail. */
const BUILTIN_IDS = new Set(BUILTIN_VIEWS.map((v) => v.id));
import { SaveViewModal } from './SaveViewModal';
import { ticketRef, TICKET_SOURCES, TYPE_LABEL, type Condition } from '@itsm/shared';
import { toClauses, type ConditionField } from '../../components/ConditionBuilder';
import type { InlineEditCtx } from '../../features/tickets/InlineCell';
import { useAuth } from '../../lib/AuthContext';
import { useOrgRealtime, TICKET_LIST_KEYS } from '../../features/tickets/useOrgRealtime';

/**
 * What the queue can filter on. Mirrors `QUEUE_FIELDS` in
 * `lib/conditionSql.ts` — the server's registry is the authority, and a field
 * offered here that it does not know is a 400 the moment somebody uses it.
 *
 * `omitOps` carries the per-field truths the grammar cannot express: SLA state
 * is an enum with no substring, and every ticket has a status, so "is empty"
 * is a question with no answer.
 */
const QUEUE_CONDITION_FIELDS: ConditionField[] = [
  {
    key: 'statusCategory',
    label: 'Status category',
    options: [
      { value: 'open', label: 'Open' },
      { value: 'pending', label: 'Pending' },
      { value: 'resolved', label: 'Resolved' },
      { value: 'closed', label: 'Closed' },
    ],
    omitOps: ['isEmpty', 'isNotEmpty'],
  },
  {
    key: 'priority',
    label: 'Priority',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'urgent', label: 'Urgent' },
    ],
  },
  {
    key: 'urgency',
    label: 'Urgency',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
    ],
  },
  {
    key: 'impact',
    label: 'Impact',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
    ],
  },
  {
    key: 'type',
    label: 'Type',
    // Every type the queue can CONTAIN — tasks included, since Kai asked for
    // those to appear as tickets. Problems and changes are excluded because
    // the default list excludes them and they have their own pages; on those
    // pages the field is dropped entirely (the type is pinned).
    options: [
      { value: 'incident', label: TYPE_LABEL.incident! },
      { value: 'service_request', label: TYPE_LABEL.service_request! },
      { value: 'task', label: TYPE_LABEL.task! },
      // Step 54 — a major incident stays in the incident QUEUE (it is the
      // same work, escalated), so unlike problem and change it is filterable
      // here rather than living behind its own route.
      { value: 'major_incident', label: TYPE_LABEL.major_incident! },
    ],
  },
  { key: 'source', label: 'Source', options: TICKET_SOURCES.map((s) => ({ value: s.key, label: s.label })) },
  {
    key: 'slaState',
    label: 'SLA',
    options: [
      { value: 'breached', label: 'Breached' },
      { value: 'response_due', label: 'First response outstanding' },
      { value: 'resolution_due', label: 'Resolution outstanding' },
      { value: 'ok', label: 'Nothing breached' },
    ],
    omitOps: ['contains', 'notContains', 'isEmpty', 'isNotEmpty'],
  },
  { key: 'subject', label: 'Subject' },
  { key: 'tag', label: 'Tag' },
  { key: 'status', label: 'Status', omitOps: ['isEmpty', 'isNotEmpty'] },
  { key: 'groupId', label: 'Group' },
  { key: 'assigneeId', label: 'Assignee' },
  // Step 53 — the server has always compiled `requesterId` and DYNAMIC_VALUES
  // already carried its "@me", but the field was never offered here. So the
  // person page's "See all in the queue" link arrived as a filter the panel
  // could not name, let alone edit.
  { key: 'requesterId', label: 'Requester' },
];

/**
 * The dynamic values, offered at the TOP of their field's option list.
 *
 * Kai: *"my groups should be a thing too, so a filter can reference My
 * groups, assigned to Me, or just My Groups"*. They are resolved by the
 * server against whoever is reading, so a shared view follows the reader —
 * and somebody who changes team keeps working views.
 */
const DYNAMIC_VALUES: Record<string, { value: string; label: string }[]> = {
  assigneeId: [
    { value: '@me', label: 'Me' },
    { value: '', label: 'Nobody (unassigned)' },
  ],
  requesterId: [{ value: '@me', label: 'Me' }],
  groupId: [{ value: '@myGroups', label: 'Any of my groups' }],
};

export function TicketsPage({
  lockedType,
  title,
}: {
  /**
   * Step 48 — pin the list to one ticket type.
   *
   * Kai: *"problems and changes should be in a separate tab."* They are rows
   * in the same table, so this is the same queue with one filter pinned —
   * `/problems` rather than a second copy of a thousand lines that would
   * drift the first time a column was added to one of them.
   */
  lockedType?: string;
  title?: string;
} = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [statusFilter] = useUrlListState('status');
  const [priorityFilter] = useUrlListState('priority');
  const [typeFilter] = useUrlListState('type');
  const [groupFilter] = useUrlState('group', null);
  const [sourceFilter] = useUrlState('source', null);
  const [itemFilter] = useUrlState('item', null);
  const [fieldKeyFilter] = useUrlState('fkey', null);
  const [fieldValueFilter] = useUrlState('fval', null);
  const [q, setQ] = useDebouncedUrlState('q');
  const [viewId, setViewId] = useUrlState('view', null);
  /** Step 111 — the recycle bin is a PLACE, so it lives in the URL: a
   *  refresh keeps you there and the link can be sent to somebody. */
  const [bin, setBin] = useUrlState('bin', null);
  /**
   * Step 42 — the ADVANCED filter, as JSON in the URL.
   *
   * Kai: *"filtering & layouts for tickets, it's really crap, and it's super
   * clumsy"*. The chips stay — they are the fast path and they keep a saved
   * view's URL readable — but everything they cannot say lives here: ALL/ANY
   * nesting, operators, tags, and SLA state, which until now made Step 31's
   * clocks decorative because nothing could filter on them.
   *
   * In the URL like every other filter (the Global Standard), so an advanced
   * filter is shareable and a saved view can carry one without a second
   * storage format.
   */
  const [filterRaw, setFilterRaw] = useUrlState('filter', null);
  const [filterOpen, setFilterOpen] = useUrlState('fopen', null);
  /**
   * The column set, in the URL — so it is part of "modified vs the view" and
   * saves with the view like every other piece of queue state. Kai: *"the
   * columns are customisable & actual columns"*.
   */
  const [colsRaw, setColsRaw] = useUrlState('cols', null);
  const [colsOpen, setColsOpen] = useState(false);
  const columnIds = useMemo(
    () => (colsRaw ? colsRaw.split(',').filter(Boolean) : DEFAULT_COLUMNS),
    [colsRaw],
  );
  /**
   * The org's FILTERABLE custom fields, offered alongside the built-in ones.
   *
   * Keyed `cf:<key>` — the prefix the compiler uses to decide "column or
   * answer" from the key alone, rather than keeping a parallel list both
   * sides have to agree about. Only fields an admin marked filterable are
   * indexed, so this list is also the permission boundary.
   */
  const { data: filterableData } = useQuery({
    queryKey: qk.filterableFields,
    queryFn: () =>
      apiFetch<{ entries: { key: string; label: string; type: string; options?: unknown }[] }>(
        '/api/tickets/filterable-fields',
      ),
    staleTime: 5 * 60_000,
  });
  const conditionFields = useMemo<ConditionField[]>(
    () => [
      // `type` is not offered when the page PINS one: the pin is applied last,
      // so a filter on it could only ever narrow to nothing, and a control
      // that can only produce an empty list is worse than no control.
      ...QUEUE_CONDITION_FIELDS.filter((f) => !lockedType || f.key !== 'type'),
      ...(filterableData?.entries ?? []).map((f) => {
        const opts = Array.isArray(f.options)
          ? (f.options as { value?: string; label?: string }[])
              .map((o) =>
                typeof o === 'string'
                  ? { value: o, label: o }
                  : { value: String(o.value ?? o.label ?? ''), label: String(o.label ?? o.value ?? '') },
              )
              .filter((o) => o.value)
          : [];
        return {
          key: `cf:${f.key}`,
          // Marked as a custom field in the picker: two fields can legitimately
          // share a label, and "Priority" the answer is not "Priority" the
          // ticket property.
          label: `${f.label} (field)`,
          ...(opts.length ? { options: opts } : {}),
        };
      }),
    ],
    [filterableData, lockedType],
  );

  const columns = useMemo(() => resolveColumns(columnIds), [columnIds]);
  /** Drag-resized widths, in the URL so they save with the view. */
  const [colWidthsRaw, setColWidthsRaw] = useUrlState('colw', null);
  const colWidths = useMemo(() => parseWidths(colWidthsRaw), [colWidthsRaw]);
  const template = useMemo(() => gridTemplate(columns, colWidths), [columns, colWidths]);
  const tableMin = useMemo(() => minTableWidth(columns, colWidths), [columns, colWidths]);

  /**
   * Column resizing.
   *
   * Listeners go on the WINDOW for the duration of the drag, not on the
   * handle: a pointer moving faster than React re-renders leaves the handle
   * behind, and a handle-bound listener then stops receiving moves halfway
   * through — the drag appears to stick. Committed to the URL on release
   * rather than on every move, so a resize is one history entry and one
   * refetch-free update instead of forty.
   */
  const dragRef = useRef<{ id: string; startX: number; startW: number } | null>(null);
  const [dragWidths, setDragWidths] = useState<Record<string, number> | null>(null);
  const startResize = useCallback(
    (e: React.PointerEvent, col: { id: string; width: number }) => {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = { id: col.id, startX: e.clientX, startW: colWidths[col.id] ?? col.width };
      const onMove = (ev: PointerEvent) => {
        const d = dragRef.current;
        if (!d) return;
        const next = Math.min(COLUMN_MAX, Math.max(COLUMN_MIN, d.startW + (ev.clientX - d.startX)));
        setDragWidths({ ...colWidths, [d.id]: next });
      };
      const onUp = (ev: PointerEvent) => {
        const d = dragRef.current;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        dragRef.current = null;
        setDragWidths(null);
        if (!d) return;
        const next = Math.min(COLUMN_MAX, Math.max(COLUMN_MIN, d.startW + (ev.clientX - d.startX)));
        setColWidthsRaw(serialiseWidths({ ...colWidths, [d.id]: next }));
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [colWidths, setColWidthsRaw],
  );
  /** The live template while dragging, so the columns follow the pointer. */
  const liveTemplate = dragWidths ? gridTemplate(columns, dragWidths) : template;

  const advanced = useMemo<Condition | undefined>(() => {
    if (!filterRaw) return undefined;
    try {
      return JSON.parse(filterRaw) as Condition;
    } catch {
      // A hand-mangled URL must not white-screen the queue.
      return undefined;
    }
  }, [filterRaw]);
  const patchUrl = useUrlPatch();
  /**
   * Whether the views rail is open. A PREFERENCE, not a place — the same
   * stated exception the app sidebar takes, because a shared link should
   * carry the FILTER and not my chrome.
   */
  const [railOpen, setRailOpen] = useState(() => {
    try {
      return localStorage.getItem('ticketViewRail') !== '0';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('ticketViewRail', railOpen ? '1' : '0');
    } catch {
      /* fine */
    }
  }, [railOpen]);
  const [savingView, setSavingView] = useState(false);
  /** The view being edited in the rail's Edit action — name, scope, groups. */
  const [editingView, setEditingView] = useState<SavedView | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const confirm = useConfirm();
  const [focusIndex, setFocusIndex] = useState<number>(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const views = useViews();
  const activeView = views.find((v) => v.id === viewId) ?? null;
  /**
   * Rows per page, from the active view — `saved_views.page_size`, which the
   * API has stored since 0053 and nothing read until now.
   *
   * It feeds the query key, so switching view refetches from page one rather
   * than leaving you 200 rows deep in a list that is now a different list.
   */
  /** `/tickets`, `/problems` or `/changes` — this list's own base, so New
   *  lands on the matching create page. */
  /**
   * Step 60 — told, not refreshed.
   *
   * The queue does NOT refetch itself: the plan's rule is that rows must never
   * move under the cursor, and a live refetch is precisely that. The count is
   * offered and the person takes it when they are ready.
   */
  const realtime = useOrgRealtime({ invalidate: TICKET_LIST_KEYS });
  const listBase = lockedType === 'problem' ? '/problems' : lockedType === 'change' ? '/changes' : '/tickets';
  const pageSize = activeView?.pageSize ?? DEFAULT_PAGE_SIZE;
  const { data: groupsData } = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups });
  // Agents, for the advanced filter's Assignee options. Cheap and cached; the
  // chips never needed it because there was no assignee chip.
  const { data: peopleData } = useQuery({
    queryKey: qk.people,
    queryFn: ticketsApi.people,
    staleTime: 5 * 60_000,
  });
  /** Step 171 — who can hold a ticket. Staff only, everywhere an assignee is
   *  offered. `/api/org/people` deliberately returns every kind, so the filter
   *  belongs at each use rather than at the fetch. */
  const assignablePeople = useMemo(
    () => (peopleData?.entries ?? []).filter((p) => p.kind === 'agent' || p.kind === 'platform'),
    [peopleData],
  );

  /* The catalogue-item list and the filterable-field definitions fed the
     chip row and nothing else. The advanced filter resolves its own options
     through `conditionValueOptions`, and custom fields arrive there when the
     registry gains its `custom` kind — so these queries went with the chips
     rather than being left running for a UI that no longer exists. */

  // The view SEEDS the URL: only when the address carries no explicit filters
  // of its own — a shared link's exact params always win over the stored view.
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (!activeView || seeded.current === activeView.id) return;
    // URL state on purpose: a POINT-IN-TIME read. The seeding rule is "patch
    // only the keys absent from the current params", which needs what the URL
    // holds at this instant, not a subscription — the hook's value is what we
    // are about to write, so reading it here would compare a value to itself.
    const current = canonicalQuery(new URLSearchParams(window.location.search));
    if (current === '') {
      const entries = Object.fromEntries(new URLSearchParams(activeView.query).entries());
      patchUrl(entries);
    }
    seeded.current = activeView.id;
  }, [activeView, patchUrl]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    // Step 111 — the bin replaces the queue rather than filtering it.
    if (bin) p.set('bin', bin);
    if (statusFilter.length) p.set('status', statusFilter.join(','));
    if (priorityFilter.length) p.set('priority', priorityFilter.join(','));
    if (typeFilter.length) p.set('type', typeFilter.join(','));
    if (groupFilter) p.set('group', groupFilter);
    if (sourceFilter) p.set('source', sourceFilter);
    if (itemFilter) p.set('item', itemFilter);
    if (fieldKeyFilter && fieldValueFilter) {
      p.set('fields', JSON.stringify([{ fieldKey: fieldKeyFilter, op: 'eq', value: fieldValueFilter }]));
    }
    if (q) p.set('q', q);
    if (filterRaw) p.set('filter', filterRaw);
    // Non-chip filters a view may carry (assignee=me/none) ride through from
    // the URL so built-ins work before dedicated chips exist.
    // URL state on purpose: a point-in-time read of filters that have no chip
    // yet (assignee=me/none), so a saved view carrying them still works.
    const url = new URLSearchParams(window.location.search);
    for (const key of ['assignee']) {
      const v = url.get(key);
      if (v) p.set(key, v);
    }
    // Pinned LAST so a saved view or a hand-edited URL cannot widen the list
    // past the type this page is for.
    if (lockedType) p.set('type', lockedType);
    p.set('limit', String(pageSize));
    return p;
  }, [
    // Step 111 — without this the bin renders the queue's rows: the memo
    // never recomputes, so `?bin=1` reaches the URL and never the request.
    bin,
    statusFilter,
    priorityFilter,
    typeFilter,
    groupFilter,
    itemFilter,
    fieldKeyFilter,
    fieldValueFilter,
    filterRaw,
    // `sourceFilter` was MISSING here — it is read inside the memo, so
    // choosing a source never rebuilt the params and the queue quietly
    // ignored the chip. Surfaced by react-hooks/exhaustive-deps the moment
    // that rule was switched on; it is not a style warning, it is a filter
    // that did nothing.
    sourceFilter,
    q,
    pageSize,
    lockedType,
  ]);

  /**
   * Cursor pagination, per the plan — NOT a growing `limit`.
   *
   * The growing-limit version had a dead end nobody could get past: the route
   * clamps `limit` at 100, so the third "Load more" re-fetched the same 100
   * rows, `cursor` stayed non-null, and the button sat there offering more
   * forever. Ticket 101 was simply unreachable from the queue.
   *
   * Cursors also fix what the clamp was there to prevent — a live queue where
   * rows move between pages under an offset both double-shows and skips. The
   * server has emitted `nextCursor` since the route was written; this is the
   * client finally using it.
   */
  const { data, isPending, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: qk.tickets.list(params.toString()),
      queryFn: ({ pageParam }) => {
        // `params` is rebuilt per page rather than mutated: mutating it would
        // leave the previous page's cursor on the object the query key was
        // derived from.
        const p = new URLSearchParams(params);
        if (pageParam) p.set('cursor', pageParam);
        return ticketsApi.list(p);
      },
      initialPageParam: '',
      getNextPageParam: (last: TicketList) => last.cursor ?? undefined,
      placeholderData: keepPreviousData,
    });
  /** Page one owns the facets, the totals and the status list: each is
   *  computed over the whole filter, not the page, so later pages just repeat
   *  them. Reading page one keeps them steady while more pages load. */
  const head = data?.pages[0];
  const rows = useMemo(() => data?.pages.flatMap((p) => p.rows) ?? [], [data]);

  /**
   * Inline row editing — Step 42's last piece. See `InlineCell` for why the
   * commit writes the cache in place and does NOT invalidate the list.
   *
   * Gated on `tickets.edit` and null otherwise: the registry treats `null` as
   * "render read-only", so a queue for someone who may only look is plain
   * text rather than controls that 403 on use.
   */
  const canEditTickets = !!me?.permissions['tickets']?.edit;
  /** Does moving to `groupId` leave `assigneeId` outside it? Mirrors the
   *  server's own rule so the optimistic write cannot disagree with it. */
  const strandsAssignee = useCallback(
    (assigneeId: string | null | undefined, groupId: unknown): boolean => {
      if (!assigneeId) return false;
      if (!groupId || typeof groupId !== 'string') return true;
      const members = groupsData?.entries.find((g) => g.id === groupId)?.memberIds ?? [];
      return !members.includes(assigneeId);
    },
    [groupsData],
  );
  const listKey = qk.tickets.list(params.toString());
  const inline = useMemo<InlineEditCtx | null>(() => {
    if (!canEditTickets) return null;
    return {
      statuses: (head?.statuses ?? []).map((st) => ({ value: st.key, label: st.name })),
      groups: (groupsData?.entries ?? []).map((g) => ({ value: g.id, label: g.name })),
      agentsFor: (groupId) => {
        if (!groupId) return [];
        const members = groupsData?.entries.find((g) => g.id === groupId)?.memberIds;
        if (!members?.length) return [];
        return (peopleData?.entries ?? [])
          .filter((p) => members.includes(p.id))
          .map((p) => ({ value: p.id, label: p.name ?? p.email }));
      },
      commit: async (ticketId, patch) => {
        const r = await ticketsApi.patch(ticketId, patch);
        if (!r.ok) {
          toast.error(r.error ?? 'Could not save that change.');
          return;
        }
        /**
         * Rewrite the one row, across whichever loaded page holds it.
         *
         * `setQueryData` rather than `invalidateQueries` on purpose — see the
         * InlineCell header. The names have to be resolved here too: the row
         * carries `assigneeName` for display and the PATCH only returned ok,
         * so without this the cell would show the old agent until a refetch.
         */
        queryClient.setQueryData<InfiniteData<TicketList>>(listKey, (prev) =>
          prev
            ? {
                ...prev,
                pages: prev.pages.map((pg) => ({
                  ...pg,
                  rows: pg.rows.map((row) =>
                    row.id === ticketId
                      ? {
                          ...row,
                          ...('status' in patch ? { status: String(patch.status) } : {}),
                          ...('assigneeId' in patch
                            ? {
                                assigneeId: (patch.assigneeId as string | null) ?? null,
                                assigneeName:
                                  (peopleData?.entries ?? []).find((p) => p.id === patch.assigneeId)?.name ??
                                  null,
                              }
                            : {}),
                          ...('groupId' in patch
                            ? {
                                groupId: (patch.groupId as string | null) ?? null,
                                groupName:
                                  (groupsData?.entries ?? []).find((g) => g.id === patch.groupId)?.name ??
                                  null,
                                /**
                                 * Moving a ticket to a group its agent is not
                                 * in UNASSIGNS it — the server does that
                                 * itself, so the cache has to agree or the row
                                 * keeps showing an agent the ticket no longer
                                 * has. Mirrored rather than refetched, because
                                 * refetching is the very thing that would make
                                 * the row disappear from under the cursor.
                                 */
                                ...(strandsAssignee(row.assigneeId, patch.groupId)
                                  ? { assigneeId: null, assigneeName: null }
                                  : {}),
                              }
                            : {}),
                        }
                      : row,
                  ),
                })),
              }
            : prev,
        );
        // The detail page and the peek pane read their own query, so they do
        // need telling.
        void queryClient.invalidateQueries({ queryKey: qk.tickets.detail(ticketId) });
      },
    };
  }, [canEditTickets, head?.statuses, peopleData, groupsData, strandsAssignee, queryClient, listKey]);

  /** What the cells need that the registry cannot know — resolved once per
   *  render rather than per cell. */
  const colCtx = useMemo(
    () => ({
      statusName: (key: string) => head?.statuses.find((s) => s.key === key)?.name ?? key,
      statusClass: (key: string) => STATUS_BADGE[key] ?? '',
      sourceLabel: (key: string | null | undefined) =>
        TICKET_SOURCES.find((s) => s.key === key)?.label ?? '—',
      // Canonical by TYPE, so opening a problem from the queue lands on
      // /problems/<id> rather than bouncing through /tickets/<id>.
      onOpen: (id: string) => {
        const row = rows.find((r) => r.id === id);
        navigate(ticketPath(row?.type ?? 'incident', id));
      },
      inline,
    }),
    [head?.statuses, navigate, inline],
  );

  /** Options for the fields whose values live in the database. The rest
   *  carry their own, in the registry above. */
  const conditionValueOptions = useCallback(
    (key: string): { value: string; label: string }[] => {
      const dynamic = DYNAMIC_VALUES[key] ?? [];
      if (key === 'groupId')
        return [...dynamic, ...(groupsData?.entries ?? []).map((g) => ({ value: g.id, label: g.name }))];
      /**
       * `/api/org/people` returns every kind, so one list serves both fields —
       * but they want DIFFERENT halves of it. A requester filter needs
       * requesters (and staff, who raise tickets too), which is what makes it
       * show a name rather than a bare uuid. An assignee filter needs only
       * people who could ever BE an assignee; offering the whole customer base
       * as values for "assigned to" is a list nobody can use.
       */
      if (key === 'assigneeId')
        return [...dynamic, ...assignablePeople.map((p) => ({ value: p.id, label: p.name ?? p.email }))];
      if (key === 'requesterId')
        return [
          ...dynamic,
          ...(peopleData?.entries ?? []).map((p) => ({ value: p.id, label: p.name ?? p.email })),
        ];
      if (key === 'status') return (head?.statuses ?? []).map((s) => ({ value: s.key, label: s.name }));
      return dynamic;
    },
    [groupsData, peopleData, head?.statuses],
  );

  // Modified-vs-view: a canonical string comparison, immune to param order.
  const currentCanonical = canonicalQuery(params);
  const viewModified = !!activeView && currentCanonical !== canonicalOf(activeView.query);
  const ownsActiveView =
    !!activeView &&
    !BUILTIN_VIEWS.some((b) => b.id === activeView.id) &&
    (activeView as { ownerId?: string }).ownerId === me?.user.id;

  const updateView = useMutation({
    mutationFn: () => apiSend(`/api/views/${activeView!.id}`, 'PATCH', { query: currentCanonical }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'Could not save the view');
      toast.success(`Saved “${activeView!.name}”.`);
      void queryClient.invalidateQueries({ queryKey: qk.views });
    },
  });

  const resetToView = useCallback(() => {
    if (!activeView) return;
    // Clear every current param except `view`, THEN apply the view's own — so
    // a filter the view doesn't set is removed, not left behind (the bug).
    const patch: Record<string, string | null> = {};
    // URL state on purpose: enumerating the keys CURRENTLY in the address bar
    // so a filter the view does not set is removed rather than left behind.
    // A subscription cannot express "what is there right now, before I write".
    for (const [k] of new URLSearchParams(window.location.search)) {
      // Chrome survives a Reset: closing the filter panel or losing your page
      // depth is not part of restoring a view's filter.
      if (!VIEW_TRANSIENT_KEYS.includes(k)) patch[k] = null;
    }
    for (const [k, v] of new URLSearchParams(activeView.query)) patch[k] = v;
    patchUrl(patch);
  }, [activeView, patchUrl]);

  // Selection + keyboard. Transient by design; the hotkeys stay dead while
  // typing in any field.
  /**
   * The anchor for shift-select — the last row clicked WITHOUT shift.
   *
   * A ref rather than state: it must be current inside the click handler that
   * is about to read it, and it changes on every click. As state it would
   * either lag by a render or re-render the whole list for something nothing
   * draws.
   */
  const rangeAnchor = useRef<string | null>(null);

  const toggleSelect = useCallback((id: string) => {
    rangeAnchor.current = id;
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * Shift-click selects the range — Step 171.
   *
   * Kai: *"no proper shift select."* Selecting sixty tickets one at a time to
   * bin them is not a workflow. The range is taken from the rows AS
   * DISPLAYED, so it follows the sort the person is looking at rather than
   * any underlying order, and it ADDS rather than replaces: shift-clicking a
   * second range keeps the first, which is what every file manager does and
   * therefore what fingers expect.
   */
  const selectRangeTo = useCallback(
    (id: string) => {
      const anchor = rangeAnchor.current;
      const ids = rows.map((r) => r.id);
      const to = ids.indexOf(id);
      const from = anchor ? ids.indexOf(anchor) : -1;
      if (to === -1 || from === -1) return toggleSelect(id);
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      setSelection((prev) => {
        const next = new Set(prev);
        for (let i = lo; i <= hi; i++) next.add(ids[i]!);
        return next;
      });
    },
    [rows, toggleSelect],
  );

  /** Every row on this page, or none — Step 171. Scoped to what is loaded, and
   *  the pill says how many, so it can never silently mean "and the 900 you
   *  cannot see". */
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selection.has(r.id));
  const toggleSelectAll = useCallback(() => {
    setSelection((prev) => {
      const next = new Set(prev);
      const every = rows.length > 0 && rows.every((r) => next.has(r.id));
      for (const r of rows) {
        if (every) next.delete(r.id);
        else next.add(r.id);
      }
      return next;
    });
  }, [rows]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable
      )
        return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, rows.length - 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && focusIndex >= 0 && rows[focusIndex]) {
        navigate(ticketPath(rows[focusIndex]!.type, rows[focusIndex]!.id));
      } else if (e.key === 'x' && focusIndex >= 0 && rows[focusIndex]) {
        toggleSelect(rows[focusIndex]!.id);
      } else if (e.key === 'Escape') {
        if (selection.size) setSelection(new Set());
        else setFocusIndex(-1);
      } else if (e.key === '/') {
        e.preventDefault();
        (document.getElementById('queue-search') as HTMLInputElement | null)?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, focusIndex, selection.size, navigate, toggleSelect]);

  useEffect(() => {
    // Keep the focused row on screen as j/k walk past the fold.
    listRef.current
      ?.querySelector<HTMLElement>(`[data-row-index="${focusIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [focusIndex]);

  const bulk = useMutation({
    mutationFn: (set: Record<string, unknown>) =>
      apiSend<{ updated: number; failures: { id: string; reason: string }[] }>('/api/tickets/bulk', 'POST', {
        ids: [...selection],
        set,
      }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'Bulk update failed');
      const { updated, failures } = res.body!;
      if (failures.length)
        toast.warning(
          `${updated} updated · ${failures.length} skipped (${failures[0]!.reason}${failures.length > 1 ? ', …' : ''})`,
        );
      else toast.success(`${updated} ticket${updated === 1 ? '' : 's'} updated.`);
      setSelection(new Set());
      void queryClient.invalidateQueries({ queryKey: qk.tickets.all });
    },
  });

  /**
   * Bin, spam and restore in bulk — Step 171.
   *
   * Kai: *"there's no bulk delete tickets."* Separate from `bulk` above
   * because it is a different endpoint with different rules, and because the
   * confirmation matters: every other control in this pill is reversible by
   * picking again, and this one asks first.
   *
   * Nothing is destroyed. It moves tickets to the bin, where they sit for
   * thirty days and can be restored; destroying still takes a second,
   * deliberate act with the admin bit behind it.
   */
  const bulkLifecycle = useMutation({
    mutationFn: (action: 'trash' | 'spam' | 'restore') =>
      apiSend<{ updated: number; failures: { id: string; number?: number; reason: string }[] }>(
        '/api/tickets/bulk/lifecycle',
        'POST',
        { ids: [...selection], action },
      ),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      const { updated, failures } = res.body!;
      if (failures.length)
        toast.warning(
          `${updated} moved · ${failures.length} skipped (${failures[0]!.reason}${failures.length > 1 ? ', …' : ''})`,
        );
      else toast.success(`${updated} ticket${updated === 1 ? '' : 's'} moved to the bin.`);
      setSelection(new Set());
      void queryClient.invalidateQueries({ queryKey: qk.tickets.all });
    },
  });

  const hasSelection = selection.size > 0;

  return (
    // Full bleed on purpose: the queue owns its own scroll container.
    // `relative` so both panels can float ABOVE the list rather than taking
    // width from it — see the rail's own note.
    /**
     * `absolute inset-0`, not `h-full`.
     *
     * This page owns its own scrolling — the list scrolls, the header and the
     * panels do not — and that needs a DEFINITE height. `h-full` resolves
     * against the ancestor's height, and inside a scrolling `main` that is
     * exactly the case where it can come out as `auto`: the page then grows
     * to fit its content, the inner `flex-1 overflow-y-auto` has nothing to
     * overflow, and nothing scrolls anywhere. Pinning to the box removes the
     * question.
     *
     * `overflow-hidden` keeps the floating panels clipped to the page rather
     * than extending it.
     */
    <div className="absolute inset-0 flex overflow-hidden">
      {/* Step 42 — the SECONDARY rail. Kai: *"just having a ticket list on
          the side bar, then a secondary side bar with all the custom views"*.
          Views moved out of the app nav because that list was every view, and
          at thirty-five it pushed the primary destinations below the fold. */}
      {railOpen && (
        <ViewRail
          views={views}
          builtinIds={BUILTIN_IDS}
          activeId={viewId}
          onPick={(v: SavedView) => {
            // Switching view CLEARS the current filters before seeding — a view
            // is a place, not a modifier, and carrying the last one's chips into
            // it is how "the view is wrong" gets reported.
            resetToView();
            setBin(null); // leaving the bin is part of going somewhere else
            setViewId(v.id);
          }}
          onNew={() => setSavingView(true)}
          onRename={(v: SavedView) => setEditingView(v)}
          onClose={() => setRailOpen(false)}
          onOpenBin={() => {
            // Same reasoning as switching view: the bin is a place, so the
            // filters you were using in the queue do not come with you.
            resetToView();
            setViewId(null);
            setBin('1');
          }}
          binActive={!!bin}
        />
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* `flex-wrap` and a smaller gap: on a narrow window this row used to
            push the search box and the buttons off the right edge (Kai: "it
            doesn't work on smaller screen sizes… I've had to expand it to
            show"). Wrapping is the honest answer for a toolbar whose contents
            grow with the state of the view. */}
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
          {!railOpen && (
            <Button size="sm" variant="ghost" onClick={() => setRailOpen(true)} aria-label="Show views">
              <PanelLeftOpen />
            </Button>
          )}
          <h1 className="truncate text-sm font-semibold">
            {activeView ? activeView.name : (title ?? 'Tickets')}
          </h1>
          {head && <span className="text-xs text-muted-foreground">{head.total}</span>}
          {/* Every list can raise the thing it lists. Problems and changes had
              no create path at all before this (Kai). A SUBPAGE, not a popup
              (Kai again) — which is also what leaves the dirty guard with one
              door to watch. */}
          {realtime.pending > 0 && (
            <Button size="sm" variant="outline" onClick={realtime.refresh}>
              <RefreshCw className="h-3.5 w-3.5" />
              {realtime.pending} update{realtime.pending === 1 ? '' : 's'}
              {realtime.lastActor ? ` · ${realtime.lastActor}` : ''}
            </Button>
          )}
          {/* Step 113 — the export carries the SAME query the list is showing
              (filters, saved view, search), so what you download is what you
              were looking at. A navigation, not a fetch: the response is a
              file, and the browser handles the download. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.location.assign(`/api/tickets?${params.toString()}&format=csv`)}
            title="Download the list as it is filtered now"
          >
            <Download /> Export CSV
          </Button>
          <Button size="sm" onClick={() => navigate(`${listBase}/new`)}>
            <Plus /> New {lockedType ? (TYPE_LABEL[lockedType] ?? lockedType).toLowerCase() : 'ticket'}
          </Button>
          {/* Always enabled on purpose: the whole group only RENDERS when
              `viewModified` is true, so the conditional render is the dirty
              gate — a disabled binding would be a second, redundant one. The
              in-flight guard is still real, to stop a double-submit. */}
          {viewModified && (
            <span className="flex items-center gap-1.5">
              <Badge variant="secondary">modified</Badge>
              {ownsActiveView && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={updateView.isPending}
                  onClick={() => updateView.mutate()}
                >
                  Save
                </Button>
              )}
              {/* Always enabled on purpose: this OPENS the save dialog, it does
                  not save. It only renders when the view is modified. */}
              <Button size="sm" variant="outline" onClick={() => setSavingView(true)}>
                Save as new
              </Button>
              <Button size="sm" variant="ghost" onClick={resetToView}>
                Reset
              </Button>
            </span>
          )}
          {/* Always enabled on purpose: opens the save dialog, and only renders
              when there are filters worth saving. */}
          {!activeView && currentCanonical !== '' && (
            <Button size="sm" variant="outline" onClick={() => setSavingView(true)}>
              <Bookmark /> Save view
            </Button>
          )}
          <div className="relative ml-2 w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="queue-search"
              className="pl-8"
              placeholder="Search subject or #ref  ( / )"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="flex-1" />
          {/* The filter panel's toggle, carrying the active-condition count —
              a hidden filter silently narrowing the queue is how somebody
              concludes work has vanished. */}
          <span className="relative">
            <Button size="sm" variant="outline" onClick={() => setColsOpen((o) => !o)}>
              <Columns3 /> Columns
            </Button>
            {colsOpen && (
              <ColumnChooser
                value={columnIds}
                onChange={(ids) =>
                  // Back to `null` when it matches the default, so an untouched
                  // queue does not carry a redundant param — and does not read
                  // as "modified" against its view.
                  setColsRaw(ids.join(',') === DEFAULT_COLUMNS.join(',') ? null : ids.join(','))
                }
                onClose={() => setColsOpen(false)}
              />
            )}
          </span>
          <Button
            size="sm"
            variant={advanced ? 'default' : 'outline'}
            onClick={() => setFilterOpen(filterOpen ? null : '1')}
          >
            <SlidersHorizontal /> Filter
            {advanced && (
              <span className="ml-1 rounded bg-background/20 px-1 text-[11px]">
                {toClauses(advanced).clauses.length}
              </span>
            )}
          </Button>
        </div>

        {/* The chip rows are GONE from the top. Kai: *"the filters being gone
            from the top & only having the advanced filters down one side"*.
            They were a fixed row per dimension, so a filter existed only if
            somebody had built a chip for it — and they cost two rows of
            vertical space on every screen to say what the side panel now says
            with operators, nesting and SLA state. Facet COUNTS were the one
            thing worth keeping, and they moved into the panel beside the
            values they describe. */}
        {/* `overflow-auto` — both axes. Once the filter takes 320px the row
            is narrower than its columns need, so the answer is a horizontal
            scrollbar rather than columns quietly disappearing off the edge.
            `min-w` on the rows is what gives it something to scroll. */}
        <div ref={listRef} className="min-h-0 flex-1 overflow-auto">
          {/* A HEADER ROW, which the list has never had: without it a value is
              only identifiable by inferring it from the shape of the row, and
              a customisable column set makes that guess impossible. */}
          {rows.length > 0 && (
            <div
              className="sticky top-0 z-10 grid items-stretch border-b bg-muted/50 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur"
              // Gap and side padding come from the constants rather than
              // `gap-3 px-4`, because `minTableWidth` has to add them up and
              // cannot read a class. See its comment for the bug.
              style={{
                gridTemplateColumns: `${GUTTER_PX}px ${liveTemplate}`,
                minWidth: tableMin,
                columnGap: COLUMN_GAP_PX,
                paddingInline: TABLE_PAD_PX / 2,
              }}
            >
              {/* One gutter track for the checkbox and the priority dot, so
                  the header lines up with the cells exactly rather than
                  nearly — two separate spacers drifted by the flex gap. It
                  holds the select-all box (Step 171): the natural place to
                  reach for it is directly above the row boxes. */}
              <span className="flex items-center">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 cursor-pointer accent-[hsl(var(--brand))]"
                  checked={allOnPageSelected}
                  ref={(el) => {
                    // Indeterminate is a property, not an attribute, so React
                    // cannot set it declaratively. Some-but-not-all selected
                    // must not read as none, or the box looks broken.
                    if (el) el.indeterminate = selection.size > 0 && !allOnPageSelected;
                  }}
                  onChange={toggleSelectAll}
                  aria-label={allOnPageSelected ? 'Clear selection' : 'Select every ticket on this page'}
                  title={allOnPageSelected ? 'Clear selection' : 'Select every ticket on this page'}
                />
              </span>
              {columns.map((col, i) => (
                <span
                  key={col.id}
                  className={cn('relative min-w-0 truncate', col.align === 'right' && 'text-right')}
                >
                  {col.label}
                  {/**
                   * The drag handle, and the VISIBLE line that says where it
                   * is. Kai: *"There needs to be lines to show the user where
                   * to drag the columns."* The first version drew a 1px
                   * `bg-border` rule the height of a 22px header row — present
                   * in the DOM and invisible in practice, so the feature was
                   * undiscoverable.
                   *
                   * Now: a full-height rule that reads as a column separator,
                   * thickening to the brand colour on hover and while
                   * dragging, so the affordance announces itself before you
                   * need it. The hit area stays 12px wide — wider than the
                   * line, because a 1px target is a target you miss.
                   *
                   * `touch-none` stops the browser claiming the gesture as a
                   * scroll.
                   */}
                  {i < columns.length - 1 && (
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${col.label}`}
                      onPointerDown={(e) => startResize(e, col)}
                      onClick={(e) => e.stopPropagation()}
                      className="group/resize absolute -right-2 top-0 z-10 flex h-full w-3 cursor-col-resize touch-none select-none justify-center"
                    >
                      <span
                        className={cn(
                          'block h-full w-px transition-all',
                          dragWidths && dragRef.current?.id === col.id
                            ? 'w-0.5 bg-brand'
                            : cn('w-0.5 group-hover/resize:bg-brand', COLUMN_RULE),
                        )}
                      />
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          {isError && (
            <div className="m-4 space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
              <p className="text-sm font-medium text-destructive">Could not load the queue</p>
              <p className="text-xs text-destructive/80">{(error as Error).message}</p>
              <button
                onClick={() => refetch()}
                className="text-xs text-destructive/80 underline hover:text-destructive"
              >
                Retry
              </button>
            </div>
          )}
          {isPending &&
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 border-b px-4 py-3">
                <Skeleton className="h-2 w-2 rounded-full" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 max-w-md flex-1" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          {rows.map((t: TicketRow, index) => {
            const pr = PRIORITY_META[t.priority];
            const selected = selection.has(t.id);
            const focused = index === focusIndex;
            return (
              <div
                key={t.id}
                data-row-index={index}
                // Mirrors the hover state for the keyboard: arrow-key row
                // focus is not hover, so without this an agent navigating by
                // keyboard would never see which cells are editable.
                data-focused={focused || undefined}
                role="row"
                aria-selected={selected}
                // The SAME grid template as the header, so the two cannot
                // drift apart — which is what a header of flex spacers beside
                // a row of flex cells was always going to do.
                style={{
                  gridTemplateColumns: `${GUTTER_PX}px ${liveTemplate}`,
                  minWidth: tableMin,
                  columnGap: COLUMN_GAP_PX,
                  paddingInline: TABLE_PAD_PX / 2,
                }}
                className={cn(
                  'group/row grid cursor-pointer items-center border-b py-2.5 transition-colors',
                  focused ? 'bg-accent/70' : 'hover:bg-accent/50',
                  selected && 'bg-brand/10',
                )}
                onClick={(e) => {
                  // Shift extends the selection from the last plain click.
                  if (e.shiftKey) {
                    e.preventDefault(); // or the browser selects the text too
                    selectRangeTo(t.id);
                    setFocusIndex(index);
                    return;
                  }
                  if (e.metaKey || e.ctrlKey) {
                    toggleSelect(t.id);
                    setFocusIndex(index);
                    return;
                  }
                  navigate(ticketPath(t.type, t.id));
                }}
              >
                {/* One gutter track holding both, so the header's single
                    24px column lines up with them exactly. */}
                <span className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => {}}
                    onClick={(e) => {
                      // The box itself takes shift too — that is where a hand
                      // goes when it means "select these", and a shift-click
                      // landing on the tick rather than beside it should not
                      // behave differently.
                      e.stopPropagation();
                      if (e.shiftKey) selectRangeTo(t.id);
                      else toggleSelect(t.id);
                    }}
                    aria-label={`Select ${ticketRef(t.type, t.number)}`}
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 accent-[hsl(var(--brand))]',
                      !hasSelection && 'opacity-0 transition-opacity group-hover/row:opacity-100',
                      focused && 'opacity-100',
                    )}
                  />
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', pr?.dot)} title={pr?.label} />
                </span>
                {/* Cells come from the ONE registry the header and the chooser
                    also read, so a column cannot exist in one and not the
                    others. */}
                {columns.map((col) => (
                  <span
                    key={col.id}
                    className={cn(
                      // `min-w-0` + `truncate` on EVERY cell: a grid track
                      // will happily let its content spill over the next one,
                      // and that overlap is exactly what Kai saw. The track
                      // sets the width; these two make the text respect it.
                      'min-w-0 truncate',
                      // A faint rule at each cell's right edge, landing on the
                      // same x as the header's separator — so the columns read
                      // as columns all the way down rather than only in the
                      // header. Skipped on the last one: a line against the
                      // window edge is noise.
                      // See COLUMN_DIVIDER for why this is muted-foreground
                      // and not `--border`.
                      'border-r pr-3 last:border-r-0 last:pr-0',
                      COLUMN_DIVIDER,
                      col.align === 'right' && 'text-right',
                      col.id !== 'subject' && 'text-xs text-muted-foreground',
                    )}
                  >
                    {col.cell(t as never, colCtx)}
                  </span>
                ))}
              </div>
            );
          })}
          {head && rows.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Nothing matches. Clear a filter, or enjoy the silence.
            </div>
          )}
          {hasNextPage && (
            <div className="flex justify-center border-b p-3">
              <Button
                size="sm"
                variant="outline"
                disabled={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
              >
                {isFetchingNextPage
                  ? 'Loading…'
                  : `Load more (${rows.length} of ${head?.total ?? rows.length})`}
              </Button>
            </div>
          )}
        </div>

        {hasSelection && (
          <div className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center">
            <div className="pointer-events-auto flex items-center gap-2 rounded-full border bg-card px-4 py-2 shadow-lg">
              <span className="text-xs font-medium">{selection.size} selected</span>
              {/* Auto-commit on purpose: this is the BULK ACTION pill. The agent has
                  already made an explicit selection and picked an action from a
                  menu — the pick IS the act, not a draft of one. Each control
                  resets to its placeholder afterwards, and the result reports
                  partial failure honestly rather than pretending it applied. */}
              <Select
                className="h-7 w-32 text-xs"
                value=""
                // Auto-commit on purpose: part of the bulk-action pill — the pick IS the act.
                onChange={(e) => e.target.value && bulk.mutate({ status: e.target.value })}
              >
                <option value="">Set status…</option>
                {(head?.statuses ?? [])
                  .filter((s) => s.key !== 'on_hold')
                  .map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.name}
                    </option>
                  ))}
              </Select>
              <Select
                className="h-7 w-32 text-xs"
                value=""
                // Auto-commit on purpose: part of the bulk-action pill — the pick IS the act.
                onChange={(e) => e.target.value && bulk.mutate({ priority: e.target.value })}
              >
                <option value="">Set priority…</option>
                {Object.entries(PRIORITY_META).map(([key, meta]) => (
                  <option key={key} value={key}>
                    {meta.label}
                  </option>
                ))}
              </Select>
              <Select
                className="h-7 w-32 text-xs"
                value=""
                // Auto-commit on purpose: part of the bulk-action pill — the pick IS the act.
                onChange={(e) =>
                  e.target.value &&
                  bulk.mutate({ groupId: e.target.value === 'none' ? null : e.target.value })
                }
              >
                <option value="">Set group…</option>
                <option value="none">No group</option>
                {(groupsData?.entries ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
              <Select
                className="h-7 w-36 text-xs"
                value=""
                // Auto-commit on purpose: part of the bulk-action pill — the pick IS the act.
                onChange={(e) =>
                  e.target.value &&
                  bulk.mutate({ assigneeId: e.target.value === 'none' ? null : e.target.value })
                }
              >
                <option value="">Set assignee…</option>
                <option value="none">Unassigned</option>
                {/* Step 171 — staff only. Kai: "in some places assignee isn't
                    guarded so it flags any requester as a ticket assignee
                    possibility." This list came straight from
                    `/api/org/people`, which returns every kind, so the bulk
                    assign offered the customers as well as the desk. */}
                {assignablePeople.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name ?? a.email}
                  </option>
                ))}
              </Select>
              <Select
                className="h-7 w-36 text-xs"
                value=""
                // Auto-commit on purpose: part of the bulk-action pill — the pick IS the act.
                onChange={(e) => e.target.value && bulk.mutate({ type: e.target.value })}
              >
                <option value="">Set type…</option>
                <option value="incident">Incident</option>
                <option value="service_request">Service request</option>
              </Select>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkLifecycle.isPending}
                onClick={async () => {
                  if (
                    !(await confirm({
                      title: `Move ${selection.size} ticket${selection.size === 1 ? '' : 's'} to the bin?`,
                      body: 'They leave the queue and can be restored. Anything left in the bin is destroyed automatically after 30 days. Nobody is emailed about this.',
                      confirmLabel: 'Move to bin',
                      destructive: true,
                    }))
                  )
                    return;
                  bulkLifecycle.mutate('trash');
                }}
              >
                <Trash2 className="h-4 w-4" />
                Bin
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelection(new Set())}
                aria-label="Clear selection"
              >
                <X />
              </Button>
            </div>
          </div>
        )}

        <SaveViewModal
          open={savingView}
          onClose={() => setSavingView(false)}
          query={currentCanonical}
          onSaved={(id) => setViewId(id)}
        />
        <ViewEditModal view={editingView} onClose={() => setEditingView(null)} />
      </div>

      {/* Step 42 — the filter DOWN THE SIDE, which is Kai's layout: *"the
          filters being gone from the top & only having the advanced filters
          down one side"*.
          A panel rather than a row buys the two things the chips could not
          have: room for operators and nesting, and room for the facet counts
          to sit BESIDE the values they describe rather than crammed onto a
          pill. It is in the URL when open, so a shared link arrives showing
          the filter that produced the list rather than a result nobody can
          account for. */}
      {filterOpen && (
        <QueueFilterPanel
          value={advanced}
          onChange={(c) => setFilterRaw(c ? JSON.stringify(c) : null)}
          onClose={() => setFilterOpen(null)}
          fields={conditionFields}
          valueOptions={conditionValueOptions}
        />
      )}
    </div>
  );
}
